import React, { useEffect, useMemo, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  AddressElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Button, Container, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { CartItem } from "../services/models";
import {
  createPaymentIntent,
  getStripeConfig,
} from "../services/storeServices";

// Inner form: rendered once Elements has a clientSecret.
// When collectShipping is true, the Address Element is mounted and Stripe
// automatically attaches the shipping address to the PaymentIntent on confirm.
function CheckoutForm({ total, collectShipping }: { total: number; collectShipping: boolean }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/order-confirmation`,
      },
    });

    // We only reach here if there's an immediate error; otherwise Stripe redirects.
    if (confirmError) {
      setError(confirmError.message ?? "Payment failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {collectShipping && (
        <div className="mb-4">
          <h5 className="mb-3">Shipping address</h5>
          <AddressElement options={{ mode: "shipping" }} />
        </div>
      )}
      <PaymentElement />
      {error && <p className="text-danger mt-3">{error}</p>}
      <Button type="submit" className="btn btn-primary mt-4 w-100" disabled={!stripe || submitting}>
        {submitting ? "Processing…" : `Pay £${total.toFixed(2)}`}
      </Button>
    </form>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const cartItems: CartItem[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("cartItems") ?? "[]");
    } catch {
      return [];
    }
  }, []);

  // Only collect a shipping address if the cart contains a physical product.
  const collectShipping = useMemo(
    () => cartItems.some((item) => !item.product.isDigital),
    [cartItems]
  );

  // Load the publishable key up front so Stripe.js is ready when we have a secret.
  useEffect(() => {
    let active = true;
    getStripeConfig()
      .then((config) => {
        if (active) setStripePromise(loadStripe(config.publishableKey));
      })
      .catch(() => {
        if (active) setError("Couldn't load payment configuration.");
      });
    return () => {
      active = false;
    };
  }, []);

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setStarting(true);
    setError(null);
    try {
      const intent = await createPaymentIntent(cartItems, email);
      setClientSecret(intent.clientSecret);
      setTotal(intent.amount / 100);
    } catch {
      setError("Couldn't start checkout. Please try again.");
    } finally {
      setStarting(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <Container className="py-5 text-center">
        <p className="text-muted">Your cart is empty.</p>
        <Button className="btn btn-primary" onClick={() => navigate("/store")}>Back to store</Button>
      </Container>
    );
  }

  return (
    <Container className="py-5" style={{ maxWidth: 520 }}>
      <h2 className="fw-bold mb-4">Checkout</h2>

      {error && <p className="text-danger">{error}</p>}

      {/* Step 1: collect email, then create the PaymentIntent. */}
      {!clientSecret ? (
        <Form onSubmit={handleStart}>
          <Form.Group className="mb-3">
            <Form.Label>Email for your receipt</Form.Label>
            <Form.Control
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Form.Group>
          <Button type="submit" className="btn btn-primary w-100" disabled={starting}>
            {starting ? "Starting…" : "Continue to payment"}
          </Button>
        </Form>
      ) : (
        /* Step 2: the Payment Element, now that we have a client secret. */
        stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm total={total} collectShipping={collectShipping} />
          </Elements>
        )
      )}
    </Container>
  );
}
